import { Formik } from 'formik'
import { useIntl, FormattedMessage, defineMessages } from 'react-intl'
import { NavLink } from 'react-router-dom'
import { isEmpty } from 'lodash'

import { Button } from 'components/v2/Button'
import { Column } from '@10xLabs/react-web/components/Column'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import Heading from 'components/v2/Heading'
import { Input } from 'components/Input'
import { InputFile } from '@10xLabs/react-web/components/InputFile'
import { Row } from '@10xLabs/react-web/components/Row'
import { InputCurrency } from 'components/InputCurrency'
import { MaskInput } from 'components/MaskInput'
import FormLayout from 'features/App/components/FormLayout'
import { getUnprocessableErrors } from 'utils/error'
import { VALID_IMAGES } from 'utils/file'
import { sanitizePrice } from 'utils/math'
import { schema } from './schema'

const messages = defineMessages({
  namePlaceholder: {
    id: 'spanish.dictionary.name',
    defaultMessage: 'Nombre'
  },
  productNamePlaceholder: {
    id: 'spanish.dictionary.product',
    defaultMessage: 'Producto'
  },
  planIdPlaceholder: {
    id: 'spanish.dictionary.planId',
    defaultMessage: 'aff3a2fb-1db6-4326-8bb9-1de179b23efa'
  }
})

const NewPlanForm = (props: TSFixMe): TSFixMe => {
  const {
    values,
    touched,
    errors,
    handleChange,
    setFieldValue,
    handleBlur,
    handleSubmit,
    error,
    s3Error,
    loading,
    isEdit
  } = props

  const intl = useIntl()

  const handleFileChange = (file: TSFixMe): TSFixMe => {
    setFieldValue('photo', file)
  }

  return (
    <form autoComplete="off" onSubmit={handleSubmit}>
      <FormLayout.CardContent>
        <Row>
          <Column>
            <Heading.H5>
              <FormattedMessage id="spanish.plan.form.general.title" defaultMessage="General" />
            </Heading.H5>
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <Input
              id="name"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.name}
              placeholder={intl.formatMessage(messages.namePlaceholder)}
              touched={touched.name}
              errors={errors.name}
              label={<FormattedMessage id="spanish.dictionary.name" defaultMessage="Nombre" />}
            />
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <MaskInput
              type="tel"
              id="planId"
              value={values.planId}
              onAccept={(value: TSFixMe): TSFixMe => setFieldValue('planId', value)}
              lazy={isEmpty(values.planId)}
              onBlur={handleBlur}
              placeholder={intl.formatMessage(messages.planIdPlaceholder)}
              mask="********-****-****-****-************"
              placeholderChar="_"
              label={<FormattedMessage id="spanish.dictionary.planId" defaultMessage="Plan id" />}
              touched={touched.planId}
              errors={errors.planId}
            />
          </Column>
        </Row>
        <Row>
          <Column>
            <InputFile
              inputProps={{
                accept: VALID_IMAGES
              }}
              value={values.photo}
              label={
                <FormattedMessage
                  id="spanish.plan.new.photo"
                  defaultMessage="Foto"
                  values={{
                    small: (msg): TSFixMe => <small>{msg}</small>
                  }}
                />
              }
              selectFileLabel={
                <FormattedMessage id="spanish.dictionary.selectFile" defaultMessage="Seleccionar archivo" />
              }
              onChange={handleFileChange}
              touched={touched.photo}
              errors={errors.photo}
            />
          </Column>
        </Row>
        <Row>
          <Column>
            <Heading.H5>
              <FormattedMessage id="spanish.dictionary.sellingPrices" defaultMessage="Precio" />
            </Heading.H5>
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <InputCurrency
              id="sellingPrice"
              label={<FormattedMessage id="spanish.dictionary.sellingPrice" defaultMessage="Precio de venta" />}
              value={values.sellingPrice}
              placeholder="$0.00"
              onChange={(value): TSFixMe => {
                setFieldValue('sellingPrice', value)
              }}
              onBlur={handleBlur}
              touched={touched.sellingPrice}
              errors={errors.sellingPrice}
              hideSuffix
            />
          </Column>
        </Row>
      </FormLayout.CardContent>
      <FormLayout.Split />
      <FormLayout.CardFooter>
        {!isEmpty(s3Error) && (
          <div>
            <ErrorMessage>{s3Error}</ErrorMessage>
          </div>
        )}
        {error && (
          <div>
            <ErrorMessage errors={getUnprocessableErrors(error)}>
              <FormattedMessage id="spanish.plan.form.error" defaultMessage="Hubo un error al agregar su plan" />
            </ErrorMessage>
          </div>
        )}
        <FormLayout.CardFooterActions>
          <Button as={NavLink} to="/snacks/plans" kind="cancel">
            <FormattedMessage id="spanish.dictionary.cancel" defaultMessage="Cancelar" />
          </Button>
          <Button type="submit" kind="primary" loading={loading}>
            {isEdit ? (
              <FormattedMessage id="spanish.dictionary.update" defaultMessage="Editar" />
            ) : (
              <FormattedMessage id="spanish.dictionary.add" defaultMessage="Agregar" />
            )}
          </Button>
        </FormLayout.CardFooterActions>
      </FormLayout.CardFooter>
    </form>
  )
}

export const NewPlan = ({ planItem = {}, onSubmit, error, loading, isEdit, s3Error }: TSFixMe): TSFixMe => {
  const intl = useIntl()

  const initialPlanItem = {
    name: planItem.name || '',
    photo: planItem.photoPath || '',
    sellingPrice: planItem.sellingPrice ? sanitizePrice(planItem.sellingPrice / 100) : '',
    planId: planItem.planID || ''
  }

  return (
    <Formik initialValues={initialPlanItem} validationSchema={schema(intl)} onSubmit={onSubmit}>
      {(props): TSFixMe => {
        return (
          <NewPlanForm
            {...props}
            isEdit={isEdit}
            error={error}
            s3Error={s3Error}
            loading={loading || props.isSubmitting}
          />
        )
      }}
    </Formik>
  )
}
